<template>
  <div class="order-detail-root">
    <v-dialog
      :value="value"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      max-width="1080"
      scrollable
      content-class="order-detail-dialog"
      @input="handleDialogInput">
      <v-card v-if="order" dark class="order-dialog-card">
        <v-card-title class="dialog-header">
          <div class="header-reference">
            <div class="header-icon">
              <v-icon color="primary">mdi-package-variant-closed</v-icon>
            </div>
            <div>
              <div class="header-eyebrow">Bestellung</div>
              <div class="header-number">{{ displayOrderNumber(order) }}</div>
              <div v-if="order.bestellnummer" class="header-internal">Intern #{{ shortId(order) }}</div>
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-chip small :color="displayStatusColor" dark class="header-status">
            <v-icon left x-small>{{ displayStatusIcon }}</v-icon
            >{{ displayStatusLabel }}
          </v-chip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon large v-bind="attrs" v-on="on" aria-label="Bestelldetail schliessen" @click="requestClose">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Schliessen</span>
          </v-tooltip>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="order-dialog-body">
          <v-alert v-if="externalUpdatePending" dense text type="warning" class="external-update-alert">
            Diese Bestellung wurde während deiner Bearbeitung aktualisiert. Dein Entwurf bleibt erhalten; prüfe ihn vor dem Speichern nochmals.
          </v-alert>

          <div class="order-layout">
            <main class="order-main">
              <section class="detail-section items-section" aria-labelledby="order-items-title">
                <div class="section-heading">
                  <div>
                    <div class="section-eyebrow">Bestellinhalt</div>
                    <h3 id="order-items-title" class="section-title">
                      Artikel
                      <span>({{ editMode ? editTotalItems : totalItems }})</span>
                    </h3>
                  </div>
                  <v-btn v-if="canEdit && !editMode" outlined color="primary" class="touch-button" @click="enterEditMode">
                    <v-icon left>mdi-pencil-outline</v-icon>Bestellung bearbeiten
                  </v-btn>
                </div>

                <div v-if="!editMode" class="items-list">
                  <div v-for="(item, idx) in order.items" :key="`${item.productTitle}-${idx}`" class="item-row">
                    <div class="item-copy">
                      <div class="item-title">
                        <v-icon v-if="item.productType === 'digital'" small color="primary">mdi-download-circle-outline</v-icon>
                        <v-icon v-else small color="grey lighten-1">mdi-package-variant</v-icon>
                        <span>{{ item.productTitle }}</span>
                      </div>
                      <div v-if="item.selectedOptions && item.selectedOptions.length > 0" class="item-options-detail">
                        <v-chip v-for="opt in item.selectedOptions" :key="opt.name" x-small outlined color="grey lighten-2">
                          {{ opt.name }}: {{ opt.value }}
                        </v-chip>
                      </div>
                    </div>
                    <div class="item-actions">
                      <v-btn
                        v-if="item.productType === 'digital' && item.file"
                        small
                        outlined
                        color="primary"
                        class="touch-button"
                        :loading="downloadingItemIdx === idx"
                        :aria-label="`${item.productTitle} herunterladen`"
                        @click="downloadItem(idx, item)">
                        <v-icon left small>mdi-download</v-icon>PDF
                      </v-btn>
                      <v-chip small color="primary" dark>{{ item.quantity }} Stk</v-chip>
                    </div>
                  </div>
                </div>

                <div v-else class="edit-area">
                  <div class="items-list edit-list">
                    <div
                      v-for="(item, idx) in editItems"
                      :key="`${item.product}-${selectionSignature(item.selectedOptions)}-${idx}`"
                      class="item-row edit-row">
                      <div class="item-copy">
                        <div class="item-title">
                          <v-icon v-if="item.productType === 'digital'" small color="primary">mdi-download-circle-outline</v-icon>
                          <v-icon v-else small color="grey lighten-1">mdi-package-variant</v-icon>
                          <span>{{ item.productTitle }}</span>
                        </div>
                        <div v-if="item.selectedOptions && item.selectedOptions.length > 0" class="item-options-detail">
                          <v-chip v-for="opt in item.selectedOptions" :key="opt.name" x-small outlined color="grey lighten-2">
                            {{ opt.name }}: {{ opt.value }}
                          </v-chip>
                        </div>
                        <div v-if="getProductStock(item) !== null" class="stock-helper">
                          {{ stockHelperLabel(item) }}
                        </div>
                      </div>

                      <div v-if="item.productType !== 'digital'" class="qty-stepper-edit" :aria-label="`Menge für ${item.productTitle}`">
                        <v-btn
                          icon
                          small
                          :disabled="item.quantity <= (item.minQuantity || 1)"
                          :aria-label="`Menge von ${item.productTitle} reduzieren`"
                          @click="changeEditQty(idx, -1)">
                          <v-icon>mdi-minus</v-icon>
                        </v-btn>
                        <input
                          v-model.number="item.quantity"
                          type="number"
                          class="qty-input"
                          :aria-label="`Menge für ${item.productTitle}`"
                          :min="item.minQuantity || 1"
                          :max="getMaxEditQty(item)"
                          @blur="clampEditQty(idx)" />
                        <v-btn
                          icon
                          small
                          :disabled="item.quantity >= getMaxEditQty(item)"
                          :aria-label="`Menge von ${item.productTitle} erhöhen`"
                          @click="changeEditQty(idx, 1)">
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                      <span v-else class="digital-quantity">1 Stk</span>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            large
                            color="error"
                            v-bind="attrs"
                            v-on="on"
                            :aria-label="`${item.productTitle} aus der Bestellung entfernen`"
                            @click="removeEditItem(idx)">
                            <v-icon>mdi-delete-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Artikel entfernen</span>
                      </v-tooltip>
                    </div>
                  </div>

                  <v-card dark flat class="add-product-card">
                    <div class="add-product-title"><v-icon small color="primary">mdi-plus-circle-outline</v-icon>Artikel hinzufügen</div>
                    <v-autocomplete
                      v-model="newProductId"
                      :items="addableProducts"
                      item-text="title"
                      item-value="_id"
                      label="Produkt suchen"
                      outlined
                      dense
                      dark
                      clearable
                      hide-details
                      @change="prepareNewProduct" />

                    <div v-if="selectedNewProduct" class="new-product-config">
                      <div v-if="selectedNewProduct.options && selectedNewProduct.options.length > 0" class="option-grid">
                        <v-select
                          v-for="opt in selectedNewProduct.options"
                          :key="opt.name"
                          :value="newProductSelections[opt.name]"
                          :items="opt.choices || []"
                          item-text="value"
                          item-value="value"
                          :label="`${opt.name} *`"
                          outlined
                          dense
                          dark
                          hide-details
                          @input="$set(newProductSelections, opt.name, $event)" />
                      </div>
                      <div class="add-product-footer">
                        <span v-if="selectedNewProductStock !== null" class="stock-helper">{{ selectedNewProductStock }} verfügbar</span>
                        <v-spacer></v-spacer>
                        <v-btn color="primary" depressed class="touch-button" :disabled="!canAddSelectedProduct" @click="addProductToEdit">
                          <v-icon left>mdi-plus</v-icon>Hinzufügen
                        </v-btn>
                      </div>
                    </div>
                  </v-card>
                </div>
              </section>

              <section class="detail-section" aria-labelledby="order-note-title">
                <div class="section-heading compact">
                  <div>
                    <div class="section-eyebrow">Zusatzinformation</div>
                    <h3 id="order-note-title" class="section-title">Bestellhinweis</h3>
                  </div>
                </div>
                <div v-if="!editMode">
                  <div v-if="order.note" class="note-block">
                    {{ order.note }}
                  </div>
                  <div v-else class="empty-inline">Kein Bestellhinweis vorhanden.</div>
                </div>
                <v-textarea
                  v-else
                  v-model="editNote"
                  label="Bestellhinweis"
                  placeholder="Anmerkungen zur Bestellung"
                  outlined
                  dense
                  dark
                  rows="3"
                  hide-details
                  auto-grow />
              </section>

              <section class="detail-section" aria-labelledby="order-comments-title">
                <div class="section-heading compact">
                  <div>
                    <div class="section-eyebrow">Kommunikation</div>
                    <h3 id="order-comments-title" class="section-title">
                      Kommentare
                      <span>({{ order.comments ? order.comments.length : 0 }})</span>
                    </h3>
                  </div>
                </div>

                <div v-if="order.comments && order.comments.length > 0" class="comments-list">
                  <v-card v-for="(comment, idx) in order.comments" :key="idx" dark flat class="comment-card">
                    <div class="comment-header">
                      <div class="comment-author">
                        <v-avatar size="30" color="blue-grey darken-2"><v-icon small>mdi-account</v-icon></v-avatar>
                        <strong>{{ comment.user ? `${comment.user.vorname} ${comment.user.nachname}` : "Unbekannt" }}</strong>
                      </div>
                      <span>{{ formatDateTime(comment.createdAt) }}</span>
                    </div>
                    <div class="comment-text">{{ comment.comment }}</div>
                  </v-card>
                </div>
                <div v-else class="empty-inline">Noch keine Kommentare.</div>

                <v-alert v-if="editMode" dense text type="info" class="mt-3 mb-0">
                  Speichere oder verwerfe die Bearbeitung, bevor du einen Kommentar sendest.
                </v-alert>
                <div v-else class="comment-composer">
                  <v-textarea
                    v-model="newComment"
                    label="Kommentar hinzufügen"
                    placeholder="Rückfrage oder Update schreiben …"
                    outlined
                    dense
                    dark
                    rows="2"
                    hide-details
                    auto-grow />
                  <div class="comment-actions">
                    <span v-if="canManage" class="notification-hint"
                      ><v-icon x-small color="grey lighten-1">mdi-bell-outline</v-icon>Der Besteller wird benachrichtigt.</span
                    >
                    <v-spacer></v-spacer>
                    <v-btn color="primary" depressed class="touch-button" :disabled="!newComment.trim()" :loading="savingComment" @click="addComment">
                      <v-icon left>mdi-send</v-icon>Kommentar senden
                    </v-btn>
                  </div>
                </div>
              </section>

              <section
                v-if="canManage && order.editHistory && order.editHistory.length > 0"
                class="detail-section"
                aria-labelledby="order-history-title">
                <div class="section-heading compact">
                  <div>
                    <div class="section-eyebrow">Nachvollziehbarkeit</div>
                    <h3 id="order-history-title" class="section-title">
                      Historie <span>({{ order.editHistory.length }})</span>
                    </h3>
                  </div>
                </div>
                <v-expansion-panels dark flat class="history-panels">
                  <v-expansion-panel v-for="(historyItem, idx) in [...order.editHistory].reverse()" :key="idx">
                    <v-expansion-panel-header>
                      <div class="history-header">
                        <div>
                          <strong>{{ historyItem.user ? `${historyItem.user.vorname} ${historyItem.user.nachname}` : "Unbekannt" }}</strong>
                          <span>{{ formatDateTime(historyItem.editedAt) }}</span>
                        </div>
                        <v-chip x-small color="blue-grey" dark>{{ actionLabel(historyItem.action) }}</v-chip>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div v-for="(change, changeIdx) in historyItem.changes" :key="changeIdx" class="history-change">
                        <strong>{{ change.field }}</strong>
                        <div class="history-values">
                          <span class="old-value">{{ formatHistoryValue(change.oldValue) }}</span>
                          <v-icon x-small color="grey">mdi-arrow-right</v-icon>
                          <span class="new-value">{{ formatHistoryValue(change.newValue) }}</span>
                        </div>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </section>
            </main>

            <aside class="order-sidebar">
              <div class="sticky-context">
                <v-card dark flat class="context-card status-context">
                  <div class="context-label">Bestellstatus</div>
                  <div class="current-status-row">
                    <span class="status-icon-large" :class="`status-${order.status}`"
                      ><v-icon color="white">{{ displayStatusIcon }}</v-icon></span
                    >
                    <div>
                      <strong>{{ displayStatusLabel }}</strong>
                      <span>{{ displayNextStepLabel }}</span>
                    </div>
                  </div>

                  <div v-if="isDigitalOrder" class="digital-ready-context">
                    <v-icon color="success">mdi-download-circle-outline</v-icon>
                    <span>Die Dateien stehen direkt bei den jeweiligen Artikeln zum Download bereit.</span>
                  </div>

                  <div v-else class="status-progress" :aria-label="`Bestellfortschritt: ${statusLabel(order.status)}`">
                    <div v-for="step in statusSteps" :key="step.value" class="status-progress-step">
                      <span
                        :class="{
                          active: isStepActive(order.status, step.value),
                          current: order.status === step.value,
                        }"></span>
                      <small>{{ step.shortLabel }}</small>
                    </div>
                  </div>

                  <div v-if="canManage && !isDigitalOrder" class="status-management">
                    <v-divider class="mb-4"></v-divider>
                    <v-alert v-if="editMode" dense text type="info" class="mb-3">Statusänderungen sind während der Bearbeitung pausiert.</v-alert>
                    <v-select
                      v-model="pendingStatus"
                      :items="statusOptions"
                      label="Neuer Status"
                      outlined
                      dense
                      dark
                      hide-details
                      :disabled="editMode || statusChanging" />
                    <v-btn
                      block
                      color="primary"
                      depressed
                      class="mt-3 touch-button"
                      :disabled="editMode || !pendingStatus || pendingStatus === order.status"
                      :loading="statusChanging"
                      @click="requestStatusChange">
                      Status aktualisieren
                    </v-btn>
                  </div>
                </v-card>

                <v-card dark flat class="context-card order-context">
                  <div class="context-label">Bestelldaten</div>
                  <dl class="info-list">
                    <div>
                      <dt>Datum</dt>
                      <dd>{{ formatDateTime(order.createdAt) }}</dd>
                    </div>
                    <div>
                      <dt>Studio</dt>
                      <dd>{{ order.studio ? order.studio.name : "-" }}</dd>
                    </div>
                    <div>
                      <dt>Bestellt von</dt>
                      <dd>{{ creatorName }}</dd>
                    </div>
                    <div v-if="order.createdBy && order.createdBy.email">
                      <dt>E-Mail</dt>
                      <dd class="email-value">{{ order.createdBy.email }}</dd>
                    </div>
                    <div v-if="order.bestellnummer || canManage" class="order-number-row">
                      <dt>Bestellnummer</dt>
                      <dd>
                        <v-text-field
                          v-if="editMode && canManage"
                          v-model="editBestellnummer"
                          placeholder="Nummer eintragen"
                          outlined
                          dense
                          dark
                          hide-details />
                        <strong v-else>{{ order.bestellnummer || "Noch nicht vergeben" }}</strong>
                      </dd>
                    </div>
                  </dl>
                </v-card>
              </div>
            </aside>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-btn v-if="canManage" text color="error" class="touch-button" :disabled="editMode || deleting" @click="deleteConfirmOpen = true">
            <v-icon left>mdi-delete-outline</v-icon>Bestellung löschen
          </v-btn>
          <v-btn
            v-if="canManage && hasPhysicalItems"
            outlined
            color="primary"
            class="touch-button"
            :loading="downloadingDelivery"
            @click="downloadDeliveryNote">
            <v-icon left>mdi-file-pdf-box</v-icon>Lieferschein
          </v-btn>
          <v-spacer></v-spacer>
          <template v-if="editMode">
            <v-btn text class="touch-button" :disabled="savingEdit" @click="requestCancelEdit">Abbrechen</v-btn>
            <v-btn color="primary" depressed class="touch-button" :loading="savingEdit" :disabled="editItems.length === 0" @click="saveEdit">
              <v-icon left>mdi-content-save-outline</v-icon>Änderungen speichern
            </v-btn>
          </template>
          <v-btn v-else text class="touch-button" @click="requestClose">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="statusConfirmOpen" max-width="480" persistent>
      <v-card dark>
        <v-card-title><v-icon left color="primary">mdi-swap-horizontal-circle-outline</v-icon>Status ändern?</v-card-title>
        <v-card-text>
          <p class="mb-3">
            Der Status wird von
            <strong>{{ statusLabel(order ? order.status : "") }}</strong> auf <strong>{{ statusLabel(pendingStatus) }}</strong> geändert.
          </p>
          <v-alert dense text :type="statusChangeDirection === 'backward' ? 'warning' : 'info'" class="mb-0">
            {{ statusChangeMessage }} Der Besteller wird per E-Mail und Push-Nachricht informiert.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="statusChanging" @click="cancelStatusChange">Abbrechen</v-btn>
          <v-btn color="primary" depressed :loading="statusChanging" @click="confirmStatusChange">Status ändern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteConfirmOpen" max-width="480" persistent>
      <v-card dark>
        <v-card-title><v-icon left color="error">mdi-alert-outline</v-icon>Bestellung löschen?</v-card-title>
        <v-card-text>
          <p>
            Du löschst
            <strong>{{ order ? displayOrderNumber(order) : "diese Bestellung" }}</strong>
            aus den aktiven Ansichten.
          </p>
          <v-alert dense text type="warning" class="mb-0"
            >Reservierter Lagerbestand wird wieder freigegeben. Diese Aktion sollte nur bei stornierten oder fehlerhaften Bestellungen verwendet
            werden.</v-alert
          >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="deleting" @click="deleteConfirmOpen = false">Abbrechen</v-btn>
          <v-btn color="error" depressed :loading="deleting" @click="deleteOrder">Bestellung löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="discardConfirmOpen" max-width="460" persistent>
      <v-card dark>
        <v-card-title><v-icon left color="warning">mdi-content-save-alert-outline</v-icon>{{ discardDialogTitle }}</v-card-title>
        <v-card-text>Deine noch nicht gespeicherten Eingaben gehen verloren.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="discardConfirmOpen = false">Weiter bearbeiten</v-btn>
          <v-btn color="warning" depressed @click="confirmDiscard">Änderungen verwerfen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="feedback.show" :color="feedback.color" :timeout="4500" bottom right>
      {{ feedback.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="feedback.show = false">Schliessen</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "OrderDetailDialog",
  props: {
    value: { type: Boolean, default: false },
    order: { type: Object, default: null },
    canManage: { type: Boolean, default: false },
    availableProducts: { type: Array, default: () => [] },
  },
  data() {
    return {
      newComment: "",
      savingComment: false,
      editMode: false,
      savingEdit: false,
      editItems: [],
      editNote: "",
      editBestellnummer: "",
      editOriginalSnapshot: "",
      externalUpdatePending: false,
      newProductId: null,
      newProductSelections: {},
      pendingStatus: null,
      statusChanging: false,
      statusConfirmOpen: false,
      deleteConfirmOpen: false,
      deleting: false,
      discardConfirmOpen: false,
      discardAction: "close",
      downloadingDelivery: false,
      downloadingItemIdx: null,
      feedback: { show: false, text: "", color: "success" },
      statusSteps: [
        {
          value: "received",
          label: "Eingegangen",
          shortLabel: "Eingang",
          icon: "mdi-inbox-arrow-down",
        },
        {
          value: "confirmed",
          label: "Bestätigt",
          shortLabel: "Bestätigt",
          icon: "mdi-check-circle-outline",
        },
        {
          value: "shipping",
          label: "Unterwegs",
          shortLabel: "Versand",
          icon: "mdi-truck-fast-outline",
        },
        {
          value: "delivered",
          label: "Geliefert",
          shortLabel: "Geliefert",
          icon: "mdi-package-variant-closed-check",
        },
      ],
      statusOptions: [
        { text: "Eingegangen", value: "received" },
        { text: "Bestätigt", value: "confirmed" },
        { text: "Unterwegs", value: "shipping" },
        { text: "Geliefert", value: "delivered" },
      ],
    }
  },
  computed: {
    totalItems() {
      return this.order ? (this.order.items || []).reduce((sum, item) => sum + (item.quantity || 0), 0) : 0
    },
    editTotalItems() {
      return (this.editItems || []).reduce((sum, item) => sum + (item.quantity || 0), 0)
    },
    creatorName() {
      if (!this.order || !this.order.createdBy) return "Unbekannt"
      return `${this.order.createdBy.vorname || ""} ${this.order.createdBy.nachname || ""}`.trim() || "Unbekannt"
    },
    canEdit() {
      if (!this.order) return false
      if (typeof this.order.canEdit === "boolean") return this.order.canEdit
      if (this.canManage) return true
      return this.order.status === "received"
    },
    addableProducts() {
      return this.availableProducts.filter((product) => {
        if (product.active === false) return false
        if ((!product.options || product.options.length === 0) && product.stock !== undefined && product.stock !== null && product.stock <= 0)
          return false
        return true
      })
    },
    selectedNewProduct() {
      if (!this.newProductId) return null
      return this.availableProducts.find((product) => String(product._id) === String(this.newProductId)) || null
    },
    selectedNewProductOptionsComplete() {
      if (!this.selectedNewProduct || !this.selectedNewProduct.options || this.selectedNewProduct.options.length === 0) return true
      return this.selectedNewProduct.options.every((option) => !!this.newProductSelections[option.name])
    },
    selectedNewProductStock() {
      if (!this.selectedNewProduct || !this.selectedNewProductOptionsComplete) return null
      return this.getProductStock({
        product: this.selectedNewProduct._id,
        selectedOptions: this.buildNewProductOptions(),
      })
    },
    canAddSelectedProduct() {
      if (!this.selectedNewProduct || !this.selectedNewProductOptionsComplete) return false
      if (this.selectedNewProductStock === null) return true
      const minimum = this.selectedNewProduct.productType === "digital" ? 1 : this.selectedNewProduct.minQuantity || 1
      return this.selectedNewProductStock >= minimum
    },
    hasPhysicalItems() {
      return !!(this.order && (this.order.items || []).some((item) => item.productType !== "digital"))
    },
    isDigitalOrder() {
      return !!(this.order && this.order.items && this.order.items.length && this.order.items.every((item) => item.productType === "digital"))
    },
    displayStatusLabel() {
      return this.isDigitalOrder ? "Downloads bereit" : this.statusLabel(this.order ? this.order.status : "")
    },
    displayStatusIcon() {
      return this.isDigitalOrder ? "mdi-download-circle-outline" : this.statusIcon(this.order ? this.order.status : "")
    },
    displayStatusColor() {
      return this.isDigitalOrder ? "green darken-1" : this.statusColor(this.order ? this.order.status : "")
    },
    displayNextStepLabel() {
      return this.isDigitalOrder ? "Der digitale Vorgang ist abgeschlossen." : this.nextStepLabel(this.order ? this.order.status : "")
    },
    currentEditSnapshot() {
      return this.buildEditSnapshot()
    },
    isEditDirty() {
      return this.editMode && ((!!this.editOriginalSnapshot && this.currentEditSnapshot !== this.editOriginalSnapshot) || !!this.newProductId)
    },
    hasUnsavedChanges() {
      return this.isEditDirty || !!this.newComment.trim()
    },
    statusChangeDirection() {
      if (!this.order || !this.pendingStatus) return "same"
      const oldIndex = this.statusSteps.findIndex((step) => step.value === this.order.status)
      const newIndex = this.statusSteps.findIndex((step) => step.value === this.pendingStatus)
      if (newIndex < oldIndex) return "backward"
      if (newIndex > oldIndex + 1) return "skip"
      return newIndex > oldIndex ? "forward" : "same"
    },
    statusChangeMessage() {
      if (this.statusChangeDirection === "backward") return "Du setzt den Prozess auf einen früheren Schritt zurück."
      if (this.statusChangeDirection === "skip") return "Du überspringst mindestens einen Prozessschritt."
      return "Die Änderung wird sofort gespeichert."
    },
    discardDialogTitle() {
      return this.discardAction === "edit" ? "Bearbeitung verwerfen?" : "Ohne Speichern schliessen?"
    },
  },
  watch: {
    value(open) {
      if (!open) this.resetTransientState()
    },
    order(nextOrder) {
      if (!nextOrder) return
      this.pendingStatus = nextOrder.status
      if (this.editMode) {
        if (this.isEditDirty) {
          this.externalUpdatePending = true
        } else {
          this.enterEditMode()
        }
      }
    },
  },
  methods: {
    shortId(order) {
      return order && order._id ? order._id.slice(-6).toUpperCase() : "------"
    },
    displayOrderNumber(order) {
      return order && order.bestellnummer ? order.bestellnummer : `#${this.shortId(order)}`
    },
    formatDateTime(date) {
      return date ? this.$moment(date).format("DD.MM.YYYY, HH:mm") : "-"
    },
    statusColor(status) {
      if (status === "delivered") return "green darken-1"
      if (status === "shipping") return "blue darken-1"
      if (status === "confirmed") return "amber darken-3"
      return "blue-grey darken-1"
    },
    statusIcon(status) {
      const step = this.statusSteps.find((item) => item.value === status)
      return step ? step.icon : "mdi-help-circle-outline"
    },
    statusLabel(status) {
      const step = this.statusSteps.find((item) => item.value === status)
      return step ? step.label : "Unbekannt"
    },
    nextStepLabel(status) {
      if (status === "received") return "Prüfung und Bestätigung stehen aus."
      if (status === "confirmed") return "Die Bestellung ist bereit für den Versand."
      if (status === "shipping") return "Die Bestellung befindet sich in Zustellung."
      if (status === "delivered") return "Der Vorgang ist abgeschlossen."
      return "Status wird geprüft."
    },
    actionLabel(action) {
      if (action === "status_change") return "Status"
      if (action === "deleted") return "Gelöscht"
      if (action === "edit") return "Bearbeitet"
      return action || "Bearbeitet"
    },
    isStepActive(currentStatus, stepValue) {
      const statuses = this.statusSteps.map((step) => step.value)
      const currentIndex = statuses.indexOf(currentStatus)
      const stepIndex = statuses.indexOf(stepValue)
      return currentIndex >= stepIndex && stepIndex >= 0
    },
    formatHistoryValue(value) {
      if (value === null || value === undefined || value === "") return "-"
      const status = this.statusSteps.find((step) => step.value === value)
      return status ? status.label : String(value)
    },
    showFeedback(text, color = "success") {
      this.feedback = { show: true, text, color }
    },
    handleDialogInput(open) {
      if (open) {
        this.$emit("input", true)
      } else {
        this.requestClose()
      }
    },
    requestClose() {
      if (this.statusChanging || this.savingEdit || this.savingComment || this.deleting) return
      if (this.hasUnsavedChanges) {
        this.discardAction = "close"
        this.discardConfirmOpen = true
        return
      }
      this.$emit("input", false)
    },
    requestCancelEdit() {
      if (this.isEditDirty) {
        this.discardAction = "edit"
        this.discardConfirmOpen = true
      } else {
        this.exitEditMode()
      }
    },
    confirmDiscard() {
      const action = this.discardAction
      this.discardConfirmOpen = false
      if (action === "edit") {
        this.exitEditMode()
        return
      }
      this.newComment = ""
      this.exitEditMode()
      this.$emit("input", false)
    },
    resetTransientState() {
      this.exitEditMode()
      this.newComment = ""
      this.statusConfirmOpen = false
      this.deleteConfirmOpen = false
      this.discardConfirmOpen = false
      this.pendingStatus = this.order ? this.order.status : null
    },
    requestStatusChange() {
      if (!this.order || !this.pendingStatus || this.pendingStatus === this.order.status) return
      this.statusConfirmOpen = true
    },
    cancelStatusChange() {
      this.statusConfirmOpen = false
      this.pendingStatus = this.order ? this.order.status : null
    },
    async confirmStatusChange() {
      if (!this.order || !this.pendingStatus || this.pendingStatus === this.order.status) return
      this.statusChanging = true
      try {
        await api.marketingShop.updateOrderStatus(this.order._id, this.pendingStatus)
        this.statusConfirmOpen = false
        this.showFeedback(`Status wurde auf „${this.statusLabel(this.pendingStatus)}“ gesetzt.`)
        this.$emit("reload")
        this.$emit("order-updated")
      } catch (error) {
        console.error("Statuswechsel fehlgeschlagen:", error)
        this.pendingStatus = this.order.status
        this.showFeedback(error.response?.data?.error || "Status konnte nicht geändert werden.", "error")
      } finally {
        this.statusChanging = false
      }
    },
    async addComment() {
      if (!this.newComment.trim() || !this.order || this.editMode) return
      this.savingComment = true
      try {
        await api.marketingShop.addOrderComment(this.order._id, this.newComment.trim())
        this.newComment = ""
        this.showFeedback("Kommentar wurde gesendet.")
        this.$emit("reload")
      } catch (error) {
        console.error("Kommentar fehlgeschlagen:", error)
        this.showFeedback(error.response?.data?.error || "Kommentar konnte nicht gesendet werden.", "error")
      } finally {
        this.savingComment = false
      }
    },
    async deleteOrder() {
      if (!this.order) return
      this.deleting = true
      try {
        await api.marketingShop.deleteOrder(this.order._id)
        this.deleteConfirmOpen = false
        this.showFeedback("Bestellung wurde gelöscht.")
        this.$emit("input", false)
        this.$emit("order-updated")
      } catch (error) {
        console.error("Löschen fehlgeschlagen:", error)
        this.showFeedback(error.response?.data?.error || "Bestellung konnte nicht gelöscht werden.", "error")
      } finally {
        this.deleting = false
      }
    },
    async downloadDeliveryNote() {
      if (!this.order) return
      this.downloadingDelivery = true
      try {
        await api.marketingShop.downloadOrderDeliveryNote(this.order._id, `Lieferschein_${this.shortId(this.order)}.pdf`)
        this.showFeedback("Lieferschein wurde heruntergeladen.")
      } catch (error) {
        console.error("Lieferschein Download fehlgeschlagen:", error)
        this.showFeedback("Lieferschein konnte nicht heruntergeladen werden.", "error")
      } finally {
        this.downloadingDelivery = false
      }
    },
    async downloadItem(idx, item) {
      this.downloadingItemIdx = idx
      try {
        const filename = item.file?.originalName || `${item.productTitle}.pdf`
        await api.marketingShop.downloadOrderItemFile(this.order._id, idx, filename)
        this.showFeedback("Download wurde gestartet.")
      } catch (error) {
        console.error("Download fehlgeschlagen:", error)
        this.showFeedback("Download konnte nicht gestartet werden.", "error")
      } finally {
        this.downloadingItemIdx = null
      }
    },
    buildEditSnapshot() {
      const items = (this.editItems || []).map((item) => ({
        product: String(item.product?._id || item.product || ""),
        quantity: item.quantity,
        selectedOptions: this.normalizedOptions(item.selectedOptions),
      }))
      return JSON.stringify({
        items,
        note: this.editNote || "",
        bestellnummer: this.editBestellnummer || "",
      })
    },
    normalizedOptions(options) {
      const list = Array.isArray(options)
        ? options
        : Object.entries(options || {}).map(([name, value]) => ({
            name,
            value,
          }))
      return list
        .map((option) => ({ name: option.name, value: option.value }))
        .sort((a, b) => `${a.name}:${a.value}`.localeCompare(`${b.name}:${b.value}`))
    },
    selectionSignature(options) {
      return this.normalizedOptions(options)
        .map((option) => `${option.name}:${option.value}`)
        .join("|")
    },
    enterEditMode() {
      if (!this.order) return
      this.editItems = (this.order.items || []).map((item) => {
        const productId = item.product?._id || item.product
        const product = this.availableProducts.find((candidate) => String(candidate._id) === String(productId))
        return {
          product: productId,
          productTitle: item.productTitle,
          productType: item.productType || "physical",
          quantity: item.quantity,
          minQuantity: product?.minQuantity || 1,
          maxQuantity: product?.maxQuantity || 100,
          selectedOptions: item.selectedOptions ? this.normalizedOptions(item.selectedOptions) : [],
        }
      })
      this.editNote = this.order.note || ""
      this.editBestellnummer = this.order.bestellnummer || ""
      this.newProductId = null
      this.newProductSelections = {}
      this.externalUpdatePending = false
      this.editMode = true
      this.editOriginalSnapshot = this.buildEditSnapshot()
    },
    exitEditMode() {
      this.editMode = false
      this.editItems = []
      this.editNote = ""
      this.editBestellnummer = ""
      this.editOriginalSnapshot = ""
      this.newProductId = null
      this.newProductSelections = {}
      this.externalUpdatePending = false
    },
    getOrderItemVariantSignature(item) {
      const productId = item.product?._id || item.product
      const product = this.availableProducts.find((candidate) => String(candidate._id) === String(productId))
      if (!product || !product.options || product.options.length === 0) return ""

      const selectedChoiceIds = []
      const selectedOptions = this.normalizedOptions(item.selectedOptions)
      for (const option of product.options) {
        const orderOption = selectedOptions.find((selected) => selected.name === option.name)
        if (!orderOption) continue
        const choice = (option.choices || []).find((candidate) => candidate.value === orderOption.value)
        if (choice && choice.id) selectedChoiceIds.push(choice.id)
      }
      return selectedChoiceIds.sort().join(",")
    },
    getProductStock(item) {
      const productId = item.product?._id || item.product
      const product = this.availableProducts.find((candidate) => String(candidate._id) === String(productId))
      if (!product) return null
      if (product.options && product.options.length > 0) {
        const signature = this.getOrderItemVariantSignature(item)
        if (!product.variantStock) return null
        const value = product.variantStock[signature]
        return value !== undefined && value !== null ? value : null
      }
      return product.stock !== undefined && product.stock !== null ? product.stock : null
    },
    getMaxEditQty(item) {
      const stock = this.getProductStock(item)
      if (stock !== null) {
        const productId = item.product?._id || item.product
        const signature = this.getOrderItemVariantSignature(item)
        const originalItem = (this.order.items || []).find((candidate) => {
          const candidateProductId = candidate.product?._id || candidate.product
          return String(candidateProductId) === String(productId) && this.getOrderItemVariantSignature(candidate) === signature
        })
        const currentQuantity = originalItem ? originalItem.quantity : 0
        return Math.min(item.maxQuantity || 100, stock + currentQuantity)
      }
      return item.maxQuantity || 100
    },
    stockHelperLabel(item) {
      const stock = this.getProductStock(item)
      if (stock === null) return ""
      const productId = item.product?._id || item.product
      const signature = this.getOrderItemVariantSignature(item)
      const isExisting = (this.order.items || []).some((candidate) => {
        const candidateProductId = candidate.product?._id || candidate.product
        return String(candidateProductId) === String(productId) && this.getOrderItemVariantSignature(candidate) === signature
      })
      return isExisting ? `${stock} zusätzlich verfügbar` : `${stock} verfügbar`
    },
    changeEditQty(idx, delta) {
      const item = this.editItems[idx]
      if (!item || item.productType === "digital") return
      const minimum = item.minQuantity || 1
      const maximum = this.getMaxEditQty(item)
      item.quantity = Math.max(minimum, Math.min(maximum, (item.quantity || minimum) + delta))
      this.$set(this.editItems, idx, { ...item })
    },
    clampEditQty(idx) {
      const item = this.editItems[idx]
      if (!item || item.productType === "digital") return
      const minimum = item.minQuantity || 1
      const maximum = this.getMaxEditQty(item)
      const value = Math.max(minimum, Math.min(maximum, parseInt(item.quantity) || minimum))
      item.quantity = value
      this.$set(this.editItems, idx, { ...item })
    },
    removeEditItem(idx) {
      this.editItems.splice(idx, 1)
    },
    prepareNewProduct() {
      this.newProductSelections = {}
    },
    buildNewProductOptions() {
      if (!this.selectedNewProduct || !this.selectedNewProduct.options) return []
      return this.selectedNewProduct.options
        .filter((option) => this.newProductSelections[option.name])
        .map((option) => ({
          name: option.name,
          value: this.newProductSelections[option.name],
        }))
    },
    addProductToEdit() {
      const product = this.selectedNewProduct
      if (!product || !this.canAddSelectedProduct) return
      const selectedOptions = this.buildNewProductOptions()
      const signature = this.selectionSignature(selectedOptions)
      const duplicate = this.editItems.some(
        (item) => String(item.product?._id || item.product) === String(product._id) && this.selectionSignature(item.selectedOptions) === signature,
      )
      if (duplicate) {
        this.showFeedback("Diese Produktvariante ist bereits enthalten. Passe ihre Menge direkt in der Liste an.", "info")
        return
      }
      this.editItems.push({
        product: product._id,
        productTitle: product.title,
        productType: product.productType || "physical",
        quantity: product.productType === "digital" ? 1 : product.minQuantity || 1,
        minQuantity: product.minQuantity || 1,
        maxQuantity: product.maxQuantity || 100,
        selectedOptions,
      })
      this.newProductId = null
      this.newProductSelections = {}
    },
    async saveEdit() {
      if (this.editItems.length === 0) {
        this.showFeedback("Die Bestellung muss mindestens einen Artikel enthalten.", "error")
        return
      }
      this.savingEdit = true
      try {
        const payload = {
          note: this.editNote || "",
          bestellnummer: this.editBestellnummer || "",
          items: this.editItems.map((item) => ({
            product: item.product?._id || item.product,
            quantity: item.quantity,
            selectedOptions: this.normalizedOptions(item.selectedOptions),
          })),
        }
        await api.marketingShop.updateOrder(this.order._id, payload)
        this.exitEditMode()
        this.showFeedback("Bestellung wurde aktualisiert.")
        this.$emit("reload")
        this.$emit("order-updated")
      } catch (error) {
        console.error("Speichern fehlgeschlagen:", error)
        this.showFeedback(error.response?.data?.error || "Bestellung konnte nicht gespeichert werden.", "error")
      } finally {
        this.savingEdit = false
      }
    },
  },
}
</script>

<style scoped>
.order-dialog-card {
  background: #1d1f21 !important;
}

.dialog-header {
  min-height: 76px;
  padding: 14px 18px;
}

.header-reference {
  display: flex;
  align-items: center;
  min-width: 0;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  margin-right: 12px;
  background: rgba(0, 150, 64, 0.12);
  border: 1px solid rgba(0, 150, 64, 0.2);
  border-radius: 12px;
}

.header-eyebrow,
.section-eyebrow,
.context-label {
  color: rgba(255, 255, 255, 0.45);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.header-number {
  overflow: hidden;
  color: #fff;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-internal {
  color: rgba(255, 255, 255, 0.42);
  font-size: 11px;
}

.header-status {
  margin-right: 8px;
}

.order-dialog-body {
  padding: 0 !important;
  background: #191b1d;
}

.external-update-alert {
  margin: 16px 16px 0;
}

.order-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  align-items: start;
  gap: 18px;
  padding: 18px;
}

.order-main,
.order-sidebar {
  min-width: 0;
}

.sticky-context {
  position: sticky;
  top: 0;
  display: grid;
  gap: 14px;
}

.detail-section,
.context-card {
  background: rgba(255, 255, 255, 0.035) !important;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 14px;
}

.detail-section {
  padding: 18px;
  margin-bottom: 16px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.context-card {
  padding: 16px;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
}

.section-heading.compact {
  margin-bottom: 12px;
}

.section-title {
  margin: 1px 0 0;
  color: #fff;
  font-size: 17px;
  font-weight: 650;
  line-height: 1.35;
}

.section-title span {
  color: rgba(255, 255, 255, 0.48);
  font-weight: 500;
}

.items-list {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 11px;
}

.edit-list {
  background: rgba(0, 150, 64, 0.035);
  border-color: rgba(0, 150, 64, 0.2);
}

.item-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 62px;
  padding: 11px 13px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.item-row:last-child {
  border-bottom: 0;
}

.item-copy {
  flex: 1;
  min-width: 0;
}

.item-title {
  display: flex;
  align-items: center;
  gap: 7px;
  color: rgba(255, 255, 255, 0.92);
  font-size: 13.5px;
  font-weight: 600;
}

.item-title span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-options-detail {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.item-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
}

.stock-helper,
.digital-quantity,
.notification-hint {
  color: rgba(255, 255, 255, 0.48);
  font-size: 11px;
}

.stock-helper {
  margin-top: 5px;
}

.qty-stepper-edit {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px;
  background: rgba(255, 255, 255, 0.065);
  border-radius: 999px;
}

.qty-input {
  width: 42px;
  height: 34px;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
  background: transparent;
  border: 0;
  outline: 0;
  -moz-appearance: textfield;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

.add-product-card {
  padding: 14px;
  margin-top: 12px;
  background: rgba(255, 255, 255, 0.025) !important;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 11px;
}

.add-product-title {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 12px;
  font-weight: 650;
}

.new-product-config {
  margin-top: 12px;
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.add-product-footer,
.comment-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}

.note-block,
.empty-inline {
  padding: 12px 14px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 13.5px;
  line-height: 1.55;
  white-space: pre-wrap;
  background: rgba(255, 255, 255, 0.025);
  border-radius: 9px;
}

.note-block {
  border-left: 3px solid #009640;
}

.empty-inline {
  color: rgba(255, 255, 255, 0.42);
  border: 1px dashed rgba(255, 255, 255, 0.09);
}

.comments-list {
  display: grid;
  gap: 8px;
}

.comment-card {
  padding: 11px 12px;
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 10px;
}

.comment-header,
.comment-author {
  display: flex;
  align-items: center;
}

.comment-header {
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.comment-author {
  min-width: 0;
  gap: 8px;
  font-size: 12.5px;
}

.comment-header > span {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.42);
  font-size: 10.5px;
}

.comment-text {
  color: rgba(255, 255, 255, 0.78);
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
}

.comment-composer {
  margin-top: 12px;
}

.notification-hint {
  display: flex;
  align-items: center;
  gap: 4px;
}

.history-panels ::v-deep .v-expansion-panel {
  background: rgba(255, 255, 255, 0.025) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.045);
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding-right: 8px;
}

.history-header > div {
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 12.5px;
}

.history-header span {
  color: rgba(255, 255, 255, 0.42);
  font-size: 10.5px;
}

.history-change {
  padding: 9px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 12px;
}

.history-change:last-child {
  border-bottom: 0;
}

.history-values {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-top: 4px;
  line-height: 1.4;
}

.old-value {
  color: #fca5a5;
}

.new-value {
  color: #86efac;
}

.current-status-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.status-icon-large {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  background: #546e7a;
  border-radius: 12px;
}

.status-icon-large.status-confirmed {
  background: #b7791f;
}

.status-icon-large.status-shipping {
  background: #1976d2;
}

.status-icon-large.status-delivered {
  background: #16803c;
}

.current-status-row > div {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.current-status-row strong {
  color: #fff;
  font-size: 15px;
}

.current-status-row span:not(.status-icon-large) {
  color: rgba(255, 255, 255, 0.5);
  font-size: 11.5px;
  line-height: 1.35;
}

.status-progress {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 5px;
  margin: 16px 0 2px;
}

.digital-ready-context {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  padding: 10px;
  border-radius: 10px;
  background: rgba(0, 150, 64, 0.1);
  color: #86efac;
  font-size: 11.5px;
  line-height: 1.45;
}

.status-progress-step {
  min-width: 0;
}

.status-progress-step > span {
  display: block;
  height: 4px;
  background: rgba(255, 255, 255, 0.09);
  border-radius: 99px;
}

.status-progress-step > span.active {
  background: #009640;
}

.status-progress-step > span.current {
  background: #4ade80;
}

.status-progress-step small {
  display: block;
  overflow: hidden;
  margin-top: 5px;
  color: rgba(255, 255, 255, 0.38);
  font-size: 9px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-management {
  margin-top: 16px;
}

.info-list {
  margin: 9px 0 0;
}

.info-list > div {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.045);
}

.info-list > div:last-child {
  border-bottom: 0;
}

.info-list dt {
  color: rgba(255, 255, 255, 0.43);
  font-size: 11.5px;
}

.info-list dd {
  min-width: 0;
  margin: 0;
  color: rgba(255, 255, 255, 0.84);
  font-size: 12.5px;
  text-align: right;
  overflow-wrap: anywhere;
}

.email-value {
  font-size: 11.5px !important;
}

.order-number-row {
  align-items: center;
}

.order-number-row strong {
  color: #86efac;
}

.dialog-actions {
  gap: 8px;
  min-height: 68px;
  padding: 10px 14px;
  background: #1d1f21;
}

.touch-button {
  min-height: 42px;
  text-transform: none;
}

@media (max-width: 900px) {
  .order-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .order-sidebar {
    grid-row: 1;
  }

  .sticky-context {
    position: static;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .dialog-header {
    min-height: 68px;
    padding: 10px 12px;
  }

  .header-icon {
    display: none;
  }

  .header-number {
    max-width: 150px;
    font-size: 16px;
  }

  .header-status {
    display: none;
  }

  .order-layout {
    gap: 12px;
    padding: 12px;
  }

  .sticky-context {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }

  .detail-section,
  .context-card {
    padding: 14px;
    border-radius: 12px;
  }

  .section-heading {
    align-items: flex-start;
  }

  .section-heading:not(.compact) {
    flex-direction: column;
  }

  .section-heading:not(.compact) .touch-button {
    width: 100%;
  }

  .item-row {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .edit-row .item-copy {
    flex-basis: calc(100% - 56px);
  }

  .item-actions {
    margin-left: auto;
  }

  .option-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .comment-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .comment-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .comment-actions .touch-button {
    width: 100%;
  }

  .dialog-actions {
    flex-wrap: wrap;
    min-height: 72px;
  }

  .dialog-actions > .v-btn {
    flex: 1 1 auto;
  }

  .dialog-actions > .v-btn:first-child:not(:last-child) {
    font-size: 12px;
  }
}

@media (max-width: 380px) {
  .info-list > div {
    grid-template-columns: minmax(0, 1fr);
    gap: 3px;
  }

  .info-list dd {
    text-align: left;
  }

  .status-progress-step small {
    font-size: 8px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}
</style>
